import { useCallback, useEffect, useState } from 'react';
import {
  hubClient,
  type CreateFormFieldDto,
  type FormCompletion,
  type FormDetail,
  type FormFieldWithValues,
} from '../services/hubClient';
import { FieldEditor } from './FieldEditor';

type CompletionKind = 'utter' | 'tool' | 'workflowId';

function completionKind(c: FormCompletion | null): CompletionKind {
  if (c?.tool) return 'tool';
  if (c?.workflowId) return 'workflowId';
  return 'utter';
}

interface Props {
  formId: string;
  /** Called after the form name/status changes so the list can refresh. */
  onChanged: () => void;
  onDeleted: () => void;
}

const labelCls = 'block text-xs font-medium text-gray-500 mb-1';
const inputCls = 'w-full rounded border border-gray-300 px-3 py-2 text-sm';

export function FormEditor({ formId, onChanged, onDeleted }: Props) {
  const [detail, setDetail] = useState<FormDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [name, setName] = useState('');
  const [cKind, setCKind] = useState<CompletionKind>('utter');
  const [cValue, setCValue] = useState('');

  const [editing, setEditing] = useState<FormFieldWithValues | null>(null);
  const [creatingField, setCreatingField] = useState(false);
  const [fieldSaving, setFieldSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await hubClient.getForm(formId);
      setDetail(d);
      setName(d.form.name);
      const k = completionKind(d.form.completion);
      setCKind(k);
      setCValue(d.form.completion?.[k] ?? '');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load form');
    } finally {
      setLoading(false);
    }
  }, [formId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function saveForm() {
    setSaving(true);
    setError(null);
    try {
      const completion: FormCompletion = cValue.trim() ? { [cKind]: cValue.trim() } : {};
      await hubClient.updateForm(formId, { name: name.trim(), completion });
      await load();
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save form');
    } finally {
      setSaving(false);
    }
  }

  async function removeForm() {
    if (!confirm('Soft-delete this form? It will be marked inactive (status=D).')) return;
    setSaving(true);
    try {
      await hubClient.deleteForm(formId);
      onDeleted();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete form');
      setSaving(false);
    }
  }

  async function saveField(dto: CreateFormFieldDto) {
    setFieldSaving(true);
    setError(null);
    try {
      if (editing) {
        await hubClient.updateField(editing.id, dto);
      } else {
        await hubClient.createField(formId, dto);
      }
      setEditing(null);
      setCreatingField(false);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save field');
    } finally {
      setFieldSaving(false);
    }
  }

  async function deleteField(id: string) {
    if (!confirm('Delete this field and its options?')) return;
    setError(null);
    try {
      await hubClient.deleteField(id);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete field');
    }
  }

  // Reorder by swapping the `ordering` of two adjacent fields (PUT both).
  async function move(field: FormFieldWithValues, dir: -1 | 1) {
    if (!detail) return;
    const sorted = [...detail.fields].sort((a, b) => a.ordering - b.ordering);
    const idx = sorted.findIndex((f) => f.id === field.id);
    const swapIdx = idx + dir;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;
    const other = sorted[swapIdx];
    setError(null);
    try {
      await hubClient.updateField(field.id, { ordering: other.ordering });
      await hubClient.updateField(other.id, { ordering: field.ordering });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to reorder');
    }
  }

  if (loading) return <div className="p-6 text-gray-500">Loading form…</div>;
  if (!detail) return <div className="p-6 text-red-600">{error ?? 'Form not found'}</div>;

  const fields = [...detail.fields].sort((a, b) => a.ordering - b.ordering);

  return (
    <div className="p-6">
      {error && (
        <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div>
      )}

      <div className="rounded-lg border border-gray-200 bg-white">
        <div className="border-b border-gray-200 px-4 py-3">
          <h2 className="text-sm font-semibold">Form settings</h2>
        </div>
        <div className="space-y-4 p-4">
          <div>
            <label className={labelCls}>Name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className={labelCls}>On completion</label>
              <select
                value={cKind}
                onChange={(e) => setCKind(e.target.value as CompletionKind)}
                className={inputCls}
              >
                <option value="utter">Utter (reply)</option>
                <option value="tool">Call tool</option>
                <option value="workflowId">Hand off to workflow</option>
              </select>
            </div>
            <div className="col-span-2">
              <label className={labelCls}>
                {cKind === 'utter'
                  ? 'Confirmation message (supports {{slot}})'
                  : cKind === 'tool'
                    ? 'Tool name'
                    : 'Workflow id / name'}
              </label>
              <input
                value={cValue}
                onChange={(e) => setCValue(e.target.value)}
                className={inputCls}
                placeholder={cKind === 'utter' ? 'Thanks {{name}}, we got it!' : ''}
              />
            </div>
          </div>
          <div className="flex justify-between">
            <button
              onClick={removeForm}
              disabled={saving}
              className="rounded px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 disabled:opacity-40"
            >
              Delete form
            </button>
            <button
              onClick={saveForm}
              disabled={saving || !name.trim()}
              className="rounded bg-blue-600 px-4 py-1.5 text-sm text-white disabled:opacity-40"
            >
              {saving ? 'Saving…' : 'Save settings'}
            </button>
          </div>
        </div>
      </div>

      <div className="mt-6 rounded-lg border border-gray-200 bg-white">
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
          <h2 className="text-sm font-semibold">Fields ({fields.length})</h2>
          <button
            onClick={() => {
              setEditing(null);
              setCreatingField(true);
            }}
            className="rounded bg-gray-100 px-3 py-1.5 text-sm hover:bg-gray-200"
          >
            + Add field
          </button>
        </div>
        <div className="divide-y divide-gray-100">
          {fields.map((f, i) => (
            <div key={f.id} className="flex items-center gap-3 px-4 py-3">
              <div className="flex flex-col">
                <button
                  onClick={() => move(f, -1)}
                  disabled={i === 0}
                  className="text-xs text-gray-400 hover:text-gray-700 disabled:opacity-30"
                  title="Move up"
                >
                  ▲
                </button>
                <button
                  onClick={() => move(f, 1)}
                  disabled={i === fields.length - 1}
                  className="text-xs text-gray-400 hover:text-gray-700 disabled:opacity-30"
                  title="Move down"
                >
                  ▼
                </button>
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{f.name}</span>
                  <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[11px] text-gray-600">
                    {f.type}
                  </span>
                  {f.isRequired && (
                    <span className="text-[11px] text-amber-600">required</span>
                  )}
                  {f.type === 'select' && (
                    <span className="text-[11px] text-gray-400">
                      {f.values.length} option{f.values.length === 1 ? '' : 's'}
                    </span>
                  )}
                </div>
                <p className="truncate text-sm text-gray-500">{f.question}</p>
              </div>
              <button
                onClick={() => {
                  setCreatingField(false);
                  setEditing(f);
                }}
                className="rounded px-2 py-1 text-sm text-blue-600 hover:bg-blue-50"
              >
                Edit
              </button>
              <button
                onClick={() => deleteField(f.id)}
                className="rounded px-2 py-1 text-sm text-gray-400 hover:bg-red-50 hover:text-red-600"
              >
                Delete
              </button>
            </div>
          ))}
          {fields.length === 0 && (
            <p className="px-4 py-6 text-sm text-gray-400">
              No fields yet. Add the first slot the bot should collect.
            </p>
          )}
        </div>
      </div>

      {(editing || creatingField) && (
        <FieldEditor
          field={editing}
          defaultOrdering={fields.length ? fields[fields.length - 1].ordering + 1 : 0}
          otherFields={fields
            .filter((f) => f.id !== editing?.id)
            .map((f) => ({ id: f.id, name: f.name }))}
          saving={fieldSaving}
          onSave={saveField}
          onClose={() => {
            setEditing(null);
            setCreatingField(false);
          }}
        />
      )}
    </div>
  );
}
